import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const STEPS = [
  {
    n: "01",
    title: "Discovery conversation",
    body: "We start with a straightforward discussion: where the land is, what you need to understand, how you plan to use the information, and any practical constraints such as access, livestock or timings.",
    detail: "You don't need any technical knowledge — describing the outcome you want is enough.",
  },
  {
    n: "02",
    title: "Scope, permissions & planning",
    body: "Before anything flies, we confirm the survey area, the capture plan, likely outputs, timescale and cost. We check airspace considerations and agree any permissions needed from landowners or occupiers.",
    detail: "Every flight is planned with the same discipline you'd expect from a professional cockpit: weather minima, risk assessment and a clear go / no-go decision.",
  },
  {
    n: "03",
    title: "Capture",
    body: "Subject to permissions, conditions and the agreed scope, the imagery is captured — methodically, with consistent altitude, overlap and coverage so the outputs are dependable.",
    detail: "If conditions aren't right, we don't fly. A rescheduled flight is always better than compromised data.",
  },
  {
    n: "04",
    title: "Processing",
    body: "Captured imagery is processed into the planned outputs — organised image sets, map-based outputs or comparison series, depending on what was agreed.",
    detail: "Processing choices follow the intended use of the outputs, not the other way round.",
  },
  {
    n: "05",
    title: "Delivery & explanation",
    body: "You receive the agreed files in practical formats, with a clear, plain-English explanation of what they show — and, just as importantly, what they don't.",
    detail: "Any limitations, such as ground cover, weather on the day or accuracy considerations, are stated openly.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <section data-testid="hiw-hero" className="bg-forest pb-20 pt-40 text-limestone md:pb-28 md:pt-52">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal><Eyebrow light>How it works</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              A calm, methodical process. No surprises.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-limestone/80 md:text-lg">
              From the first conversation to the final files, you'll always know what happens next, what it
              depends on, and what you'll receive.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-28">
        <div className="space-y-2">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.05 * i}>
              <div data-testid={`hiw-step-${i}`} className="grid gap-4 border-b border-forest/15 py-10 md:grid-cols-12 md:gap-8">
                <div className="md:col-span-2">
                  <span className="font-serif text-5xl italic text-ochre/70">{s.n}</span>
                </div>
                <div className="md:col-span-10">
                  <h2 className="font-serif text-2xl text-forest md:text-3xl">{s.title}</h2>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal/85">{s.body}</p>
                  <p className="mt-4 border-l-2 border-sage pl-4 text-sm italic leading-relaxed text-charcoal/70">
                    {s.detail}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 bg-forest p-10 text-limestone md:p-14">
            <h2 className="font-serif text-2xl md:text-3xl">Ready when you are.</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-limestone/80">
              The first conversation costs nothing and commits you to nothing.
            </p>
            <Link
              to="/contact"
              data-testid="hiw-page-cta"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-ochre px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-forest-deep transition-colors hover:bg-limestone"
            >
              Start a conversation
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
